import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';
import { useDianaUIPortal } from '../../ui/index.js';
import {
  hasLaterShootEdits,
  shootDifferences,
  type ShootComparison,
  type ShootDraft,
} from './shoot-conflict.js';
import './ShootConflictDialog.css';

export function ShootConflictDialog({
  open,
  comparison,
  latest,
  clientNames,
  onAcceptServer,
  onReapplyLocal,
  onCopyLocal,
  onRestoreFocus,
}: {
  open: boolean;
  comparison: ShootComparison | null;
  latest: ShootDraft;
  clientNames: ReadonlyMap<string, string>;
  onAcceptServer(): void;
  onReapplyLocal(): void;
  onCopyLocal(): Promise<void>;
  onRestoreFocus(): void;
}) {
  const portal = useDianaUIPortal();
  const [feedback, setFeedback] = useState('');
  const title = useRef<HTMLHeadingElement>(null);
  const differences = comparison ? shootDifferences(comparison, clientNames) : [];
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-conflict__overlay" />
        <Dialog.Content
          className="shoot-conflict"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            setFeedback('');
            title.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onRestoreFocus();
          }}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <p className="shoot-conflict__eyebrow">Revision conflict</p>
          <Dialog.Title ref={title} tabIndex={-1}>
            This shoot changed on another device
          </Dialog.Title>
          <Dialog.Description>
            Your local draft is still here. Use the server version to replace your entire local
            draft, or reapply your entire local draft to replace the newer version. These actions
            replace all shoot fields, including linked clients and checklist items.
          </Dialog.Description>
          <div className="shoot-conflict__comparison">
            <div className="shoot-conflict__columns">
              <span>Newer version</span>
              <span>Submitted edit</span>
            </div>
            {differences.map(({ key, label, current, submitted }) => (
              <section key={key} aria-label={`${label} difference`}>
                <h3>{label}</h3>
                <div className="shoot-conflict__columns">
                  <p>{current}</p>
                  <p>{submitted}</p>
                </div>
              </section>
            ))}
            {!differences.length ? <p>The writable fields match.</p> : null}
          </div>
          {comparison && hasLaterShootEdits(comparison, latest) ? (
            <p>
              You continued editing after submitting. Copy and reapply include those later local
              edits too.
            </p>
          ) : null}
          <div className="shoot-conflict__actions">
            <button
              type="button"
              className="shoot-button"
              onClick={() => {
                void onCopyLocal().then(
                  () => setFeedback('Local draft copied'),
                  () => setFeedback('Could not copy local draft. Try again.'),
                );
              }}
            >
              Copy local text
            </button>
            <button type="button" className="shoot-button" onClick={onAcceptServer}>
              Use server version
            </button>
            <button
              type="button"
              className="shoot-button shoot-button--primary"
              onClick={onReapplyLocal}
            >
              Reapply my draft
            </button>
          </div>
          {feedback ? <p role="status">{feedback}</p> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
